// FAQs.jsx
import React, { useEffect, useState } from "react"
import { Disclosure } from "@headlessui/react"
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline"
import ReactMarkdown from "react-markdown"
import { usePermissionsList } from "../../hooks/queries/usePermissions"

export const faqs = [
	// Charge Nurse FAQs
	{
		question: "What factors does the system consider when suggesting patient assignments?",
		answer: "**Patient-Specific Factors**: Acuity levels, infusion types, and one-to-one monitoring requirements.\n\n**Nurse-Specific Factors**: Shift timing, training, eligibility, lunch breaks, and proximity to rooms.\n\n**Chair-Specific Factors**: Room turnover buffers, procedure eligibility, and room preferences.\n\n**IC-Wide Constraints**: Nurse-patient ratios, safety rules, and operating hours.",
		permission: "write:schedules"
	},
	{
		question:
			"What inputs and timestamps does the IC tracker use to update throughout the day?",
		answer: "**Suggested Assignments**: Based on scheduled start/end times from booking (day before).\n\n**Live Updates**: Refreshes every 10 minutes using:\n\n- Start times (from triage or vitals).\n\n- End times (when 'dirty dot' is placed/discharged).",
		permission: "write:schedules"
	},
	{
		question: "What guidelines should the charge nurse follow to finalize assignments?",
		answer: "- Adjust for last-minute changes (e.g., sick calls, early departures).\n\n- Balance acuity points to avoid overload for any nurse.\n\n- Make clinical adjustments for high-acuity patients or specialized needs.\n\n- Ensure new staff have manageable workloads.",
		permission: "write:schedules"
	},
	{
		question: "How can the team ensure accurate acuity assignments?",
		answer: "- Promptly update the 'dirty dot' after each encounter.\n\n- Provide accurate scheduled start/end times in the booking system.\n\n- Review assignments regularly for updates or corrections.",
		permission: "write:schedules"
	},
	// Floor Nurse FAQs
	{
		question: "What factors does the system consider when suggesting patient assignments?",
		answer: "**Patient-Specific Factors**: Acuity levels, infusion types, and one-to-one monitoring.\n\n**Nurse-Specific Factors**: Shift timing, eligibility, training, and room proximity.\n\n**Chair-Specific Factors**: Room turnover buffers, isolation needs, and procedure eligibility.\n\n**IC-Wide Constraints**: Operating hours, safety rules, and patient-nurse ratios.",
		permission: "read:schedule-personal"
	},
	{
		question: "What inputs and timestamps does the IC tracker use for updates?",
		answer: "**Suggested Assignments**: Scheduled start/end times (day before).\n\n**Live Updates**: Refreshes every 10 minutes with:\n\n- Start times (from triage or vitals).\n\n- End times (when 'dirty dot' is placed/discharged).",
		permission: "read:schedule-personal"
	},
	{
		question: "How can I flag an acuity change for the CN's reference?",
		answer: "- Use the Floor Nurse portal to submit a flag for review.\n\n- Ensure accurate details are entered for timely approvals.",
		permission: "write:schedule-personal"
	},
	{
		question: "How can I complete a patient handoff around a break?",
		answer: "- Request handoff adjustments via the Floor Nurse portal.\n\n- Update the live tracker after the handoff is complete.",
		permission: "write:schedule-personal"
	},
	// Scheduler FAQs
	{
		question: "How should we handle changes to patient arrival times?",
		answer: "- Consistent schedules are often shifted within 20 minutes of the original time.\n\n- If no exact match is available, the system offers flexible options.\n\n- Use provided guidance to communicate changes effectively with families.",
		permission: "read:templates"
	},
	{
		question: "How were the new templates configured?",
		answer: "- Designed based on a two-year analysis of historical data.\n\n- Templates reflect typical infusion durations and day-specific appointment volumes.\n\n- Day-specific designs optimize scheduling efficiency.",
		permission: "read:templates"
	},
	{
		question: "Why have infusion durations changed?",
		answer: "- Adjusted to align with historical treatment times for accuracy.\n\n- Example: A 180-minute infusion may now be 140 or 300 minutes.\n\n- Buffers were added to better accommodate patient needs.",
		permission: "read:templates"
	},
	{
		question: "What changes are in place for holidays?",
		answer: "- Custom templates align with reduced operating hours and staffing.\n\n- Example: On an 8-hour holiday schedule, fewer slots are available.",
		permission: "read:templates"
	}
]

const FAQs = ({ selectedFaq }) => {
	const [openFaq, setOpenFaq] = useState(null)
	const permissions = usePermissionsList()

	useEffect(() => {
		if (selectedFaq) {
			setOpenFaq(selectedFaq)
		}
	}, [selectedFaq])

	return (
		<div id="faqs" className="bg-white py-16 sm:py-24 lg:py-32">
			<div className="max-w-7xl mx-auto px-6 lg:px-8">
				<div className="max-w-4xl mx-auto divide-y divide-gray-200">
					<h2 className="text-3xl font-bold leading-10 tracking-tight text-gray-900">
						Frequently Asked Questions
					</h2>
					<dl className="mt-10 space-y-6 divide-y divide-gray-200">
						{faqs.map(
							faq =>
								permissions &&
								permissions.includes(faq.permission) && (
									<Disclosure
										as="div"
										key={faq.question}
										className="pt-6"
										open={openFaq === faq.question}
									>
										{({ open }) => (
											<>
												<dt>
													<Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-900">
														<span className="text-base font-semibold leading-7">
															{faq.question}
														</span>
														<span className="ml-6 flex h-7 items-center">
															{open ? (
																<MinusIcon
																	className="h-6 w-6"
																	aria-hidden="true"
																/>
															) : (
																<PlusIcon
																	className="h-6 w-6"
																	aria-hidden="true"
																/>
															)}
														</span>
													</Disclosure.Button>
												</dt>
												<Disclosure.Panel as="dd" className="mt-2 pr-12">
													<div className="prose prose-slate prose-sm text-black prose-li:text-black">
														<ReactMarkdown>{faq.answer}</ReactMarkdown>
													</div>
												</Disclosure.Panel>
											</>
										)}
									</Disclosure>
								)
						)}
					</dl>
				</div>
			</div>
		</div>
	)
}

export default FAQs
